import {
  isCountable,
  itemUoms,
  type BarcodeDto,
  type BarcodeLookupDto,
  type ItemDetailDto,
  type ItemDto,
  type MasterDeltaDto,
} from '@bnn/shared';
import { api, ApiError } from '../lib/api';
import { idbClear, idbGetAll, idbPutMany } from '../lib/idb';
import { kv } from '../lib/kv';

/**
 * Master data the scan screen needs, cached on the device (Requirements §9.2):
 * items, UoM groups (with factors) and barcodes. Lookups and search work offline.
 * Refreshed with GET /master/delta — the first time in full, afterwards only changes.
 */

type CachedItem = MasterDeltaDto['items'][number];
type CachedGroup = MasterDeltaDto['uom_groups'][number];
type CachedBarcode = MasterDeltaDto['barcodes'][number];

export type BarcodeResult =
  | { kind: 'found'; data: BarcodeLookupDto }
  | { kind: 'not_found' }
  /** not in the cache and the server cannot be reached */
  | { kind: 'offline' };

const SINCE_KEY = 'master_since';
const SYNCED_AT_KEY = 'master_synced_at';
/** Warn when the cache is older than this (Requirements §9.2). */
export const MASTER_STALE_MS = 24 * 60 * 60 * 1000;

let items = new Map<string, CachedItem & { key: string }>();
let groups = new Map<string, CachedGroup>();
let barcodes = new Map<string, CachedBarcode>();
let loaded: Promise<void> | null = null;
let syncing: Promise<MasterStatus> | null = null;
const listeners = new Set<() => void>();

export interface MasterStatus {
  synced_at: string | null;
  item_count: number;
  barcode_count: number;
}

const searchKey = (i: CachedItem) => `${i.item_code} ${i.item_name}`.toLowerCase();

function index(list: CachedItem[], gs: CachedGroup[], bs: CachedBarcode[]) {
  for (const i of list) items.set(i.item_code.toUpperCase(), { ...i, key: searchKey(i) });
  for (const g of gs) groups.set(g.uom_group_code, g);
  for (const b of bs) barcodes.set(b.barcode, b);
}

/** Load the cache from IndexedDB into memory (once). */
export function loadMaster(): Promise<void> {
  loaded ??= (async () => {
    const [i, g, b] = await Promise.all([idbGetAll<CachedItem>('items'), idbGetAll<CachedGroup>('uom_groups'), idbGetAll<CachedBarcode>('barcodes')]);
    index(i, g, b);
  })().catch(() => {
    loaded = null; // IndexedDB unavailable: work online only
  });
  return loaded ?? Promise.resolve();
}

export async function masterStatus(): Promise<MasterStatus> {
  await loadMaster();
  return { synced_at: (await kv.get<string>(SYNCED_AT_KEY)) ?? null, item_count: items.size, barcode_count: barcodes.size };
}

export function onMasterChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Pull changes from the server (full download the first time). Throws when offline. */
export function syncMaster(full = false): Promise<MasterStatus> {
  syncing ??= (async () => {
    await loadMaster();
    const since = full ? undefined : await kv.get<string>(SINCE_KEY);
    const d = await api.get<MasterDeltaDto>(`/master/delta${since ? `?since=${encodeURIComponent(since)}` : ''}`);
    if (d.full) {
      await Promise.all([idbClear('items'), idbClear('uom_groups'), idbClear('barcodes')]);
      items = new Map();
      groups = new Map();
      barcodes = new Map();
    }
    await idbPutMany('items', d.items);
    await idbPutMany('uom_groups', d.uom_groups);
    await idbPutMany('barcodes', d.barcodes);
    index(d.items, d.uom_groups, d.barcodes);
    await kv.set(SINCE_KEY, d.server_time);
    await kv.set(SYNCED_AT_KEY, new Date().toISOString());
    listeners.forEach((fn) => fn());
    return masterStatus();
  })().finally(() => {
    syncing = null;
  });
  return syncing;
}

const countable = (i: CachedItem) => isCountable(i);

const toItemDto = (i: CachedItem): ItemDto => ({
  item_code: i.item_code,
  item_name: i.item_name,
  item_group: i.item_group,
  uom_group_code: i.uom_group_code,
  inventory_uom: i.inventory_uom,
  preferred_uoms: i.preferred_uoms,
  is_active: i.is_active,
  is_inventory_item: i.is_inventory_item,
  countable: countable(i),
});

/** Item + units (exact factors) + barcodes, built from the cache. */
function detailFromCache(i: CachedItem): ItemDetailDto {
  const g = i.uom_group_code ? groups.get(i.uom_group_code) : undefined;
  const lines = g?.lines.map(([uom_code, base_qty, alt_qty]) => ({ uom_code, base_qty, alt_qty })) ?? null;
  const names = new Map(g?.lines.map((l) => [l[0], l[3]]) ?? []);
  return {
    ...toItemDto(i),
    uoms: itemUoms(i.inventory_uom, lines, i.preferred_uoms).map((u) => ({
      uom_code: u.uom_code,
      uom_name: names.get(u.uom_code) ?? null,
      factor: u.factor,
      factor_num: u.num.toString(),
      factor_den: u.den.toString(),
      is_inventory: u.is_inventory,
      preferred: u.preferred,
    })),
    barcodes: [...barcodes.values()]
      .filter((b) => b.item_code === i.item_code)
      .map((b) => ({ id: b.id, barcode: b.barcode, uom_code: b.uom_code, barcode_type: b.barcode_type, supplier: b.supplier, manufacturer: b.manufacturer, is_active: b.is_active, source: b.source })),
  };
}

function lookupFromCache(b: CachedBarcode): BarcodeLookupDto | null {
  const i = items.get(b.item_code.toUpperCase());
  if (!i) return null;
  const d = detailFromCache(i);
  return {
    id: b.id,
    barcode: b.barcode,
    is_active: b.is_active,
    barcode_type: b.barcode_type,
    supplier: b.supplier,
    uom_code: b.uom_code,
    factor: d.uoms.find((u) => u.uom_code === b.uom_code)?.factor ?? null,
    item: toItemDto(i),
  };
}

/** Barcode -> item/unit: cache first; the server only for barcodes bound elsewhere and not yet cached. */
export async function lookupBarcode(code: string): Promise<BarcodeResult> {
  await loadMaster();
  const hit = barcodes.get(code);
  const fromCache = hit && lookupFromCache(hit);
  if (fromCache) return { kind: 'found', data: fromCache };
  try {
    const data = await api.get<BarcodeLookupDto>(`/barcodes/lookup/${encodeURIComponent(code)}`);
    void syncMaster().catch(() => undefined); // bring the new barcode (and anything else) into the cache
    return { kind: 'found', data };
  } catch (e) {
    if (e instanceof ApiError && e.code === 'BARCODE_NOT_FOUND') return { kind: 'not_found' };
    if (e instanceof ApiError && e.code === 'NETWORK') return { kind: 'offline' };
    throw e;
  }
}

/** Item with units + barcodes; null if no such item code. Works offline once the cache is filled. */
export async function getItem(code: string, fresh = false): Promise<ItemDetailDto | null> {
  await loadMaster();
  const hit = items.get(code.toUpperCase());
  if (hit && !fresh) return detailFromCache(hit);
  try {
    return await api.get<ItemDetailDto>(`/items/${encodeURIComponent(code)}`);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    if (e instanceof ApiError && e.code === 'NETWORK' && hit) return detailFromCache(hit);
    throw e;
  }
}

/** Search by code or name (several words = AND); countable items first. Offline-capable. */
export async function searchItems(query: string, limit = 30): Promise<ItemDto[]> {
  await loadMaster();
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];
  if (items.size === 0) {
    const r = await api.get<{ data: ItemDto[] }>(`/items?search=${encodeURIComponent(query)}&page_size=${limit}`);
    return r.data;
  }
  const out: ItemDto[] = [];
  for (const i of items.values()) {
    if (terms.every((t) => i.key.includes(t))) out.push(toItemDto(i));
  }
  return out.sort((a, b) => Number(b.countable) - Number(a.countable) || a.item_code.localeCompare(b.item_code)).slice(0, limit);
}

/** A barcode was just bound on this device: make it usable offline right away. */
export async function rememberBarcode(b: BarcodeDto) {
  const row: CachedBarcode = {
    id: b.id,
    barcode: b.barcode,
    item_code: b.item_code,
    uom_code: b.uom_code,
    barcode_type: b.barcode_type,
    supplier: b.supplier,
    manufacturer: b.manufacturer,
    is_active: b.is_active,
    source: b.source,
  };
  barcodes.set(row.barcode, row);
  await idbPutMany('barcodes', [row]).catch(() => undefined);
  listeners.forEach((fn) => fn());
}

/** Kept for API compatibility with M2 callers. */
export function forgetItem(_code: string) {}
